import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { FOUNDER, COMPANY, TIMELINE, IN_THE_ROOM, CLIENTS, INDUSTRY_SERVICE } from "@/data/content";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        kicker="About"
        title='Naomi <span class="text-warm-600">Lennon</span>.'
        intro="One of the first creator managers in the world. Fifteen years building creator businesses — from YouTube channels and creator-led brands to bestselling books, IP, and feature film. Today, Naomi runs LENNON — a single brand with two complementary divisions: Lennon Management (creator-facing) and Lennon Global (company-facing)."
      />

      <section className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14 pb-24">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16">
          <Reveal className="md:col-span-5">
            <div className="aspect-[3/4] overflow-hidden bg-mute">
              <img src={FOUNDER.image} alt={FOUNDER.name} className="w-full h-full object-cover" />
            </div>
            <div className="mt-6">
              <div className="eyebrow mb-2">Founder</div>
              <div className="display text-ink text-3xl">{FOUNDER.name}</div>
              <div className="text-warm-600 text-base mt-1">{FOUNDER.title}</div>
            </div>
          </Reveal>
          <Reveal className="md:col-span-7 md:pt-12" delay={150}>
            <div className="space-y-6 text-ink-800 text-lg leading-relaxed">
              {FOUNDER.bio.map((p, i) => (
                <p key={i} className={i === 0 ? "display text-2xl md:text-3xl text-ink leading-snug" : ""}>{p}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SELECTED CLIENTS & PARTNERS — the proof of expertise, not access */}
      <section data-testid="about-clients" className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-10 mb-12 md:mb-16 items-end">
            <div className="md:col-span-7">
              <div className="eyebrow mb-4">Selected Clients &amp; Partners</div>
              <h2 className="display text-ink" style={{ fontSize: "clamp(36px, 5.5vw, 88px)" }}>
                Brands. Platforms.<br/>Publishers.
              </h2>
            </div>
            <div className="md:col-span-5 text-warm-600 text-base md:text-lg leading-relaxed">
              Consumer brands, technology companies, global platforms, and
              publishers — the firms Lennon has built campaigns, partnerships,
              and creator strategy for.
            </div>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-line border-t border-line">
            {CLIENTS.map((name) => (
              <li
                key={name}
                data-testid={`about-client-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-paper px-4 py-6 md:py-8 flex items-center justify-center text-center"
              >
                <span className="display text-ink text-lg md:text-xl leading-tight" style={{ letterSpacing: "-0.01em" }}>
                  {name}
                </span>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* CAREER TIMELINE — the main credibility section */}
      <section data-testid="about-timeline" className="bg-ink text-paper py-24 md:py-32">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14">
          <Reveal>
            <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
              <div className="md:col-span-7">
                <div className="eyebrow text-warm-400 mb-4">Career timeline</div>
                <h2 className="display text-paper" style={{ fontSize: "clamp(40px, 6vw, 96px)" }}>
                  In the room while the<br/>industry was being built.
                </h2>
              </div>
            </div>
          </Reveal>
          <ol className="border-t border-paper/15">
            {TIMELINE.map((t, i) => (
              <Reveal as="li" key={t.year} delay={i * 80}>
                <div className="grid grid-cols-12 gap-6 md:gap-10 border-b border-paper/15 py-10 md:py-14 items-start">
                  <div className="col-span-3 md:col-span-2 display text-paper" style={{ fontSize: "clamp(28px, 3vw, 56px)" }}>
                    {t.year}
                  </div>
                  <div className="col-span-9 md:col-span-4 display text-paper" style={{ fontSize: "clamp(22px, 2.4vw, 36px)" }}>
                    {t.title}
                  </div>
                  <div className={`col-span-12 ${t.image ? "md:col-span-4" : "md:col-span-6"} text-warm-400 text-base md:text-lg leading-relaxed`}>
                    {t.body}
                  </div>
                  {t.image && (
                    <div className="col-span-12 md:col-span-2">
                      <div className="aspect-[3/4] overflow-hidden bg-paper/5">
                        <img
                          src={t.image}
                          alt={t.imageAlt || t.title}
                          className="w-full h-full object-cover grayscale opacity-90 hover:grayscale-0 hover:opacity-100 transition-all duration-500"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* INDUSTRY SERVICE — peer-elected committee positions. Proof Naomi
          is one of the voters shaping the creator economy, not an attendee. */}
      <section data-testid="about-industry-service" className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14 py-24 md:py-32 border-t border-line">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-10 mb-12 md:mb-16 items-end">
            <div className="md:col-span-7">
              <div className="eyebrow mb-4">Industry Service</div>
              <h2 className="display text-ink" style={{ fontSize: "clamp(36px, 5.5vw, 88px)" }}>
                On the committees<br/>that decide.
              </h2>
            </div>
            <div className="md:col-span-5 text-warm-600 text-base md:text-lg leading-relaxed">
              Peer-elected positions on the industry bodies that select who
              enters the creator economy&rsquo;s record book.
            </div>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <ul className="grid md:grid-cols-2 gap-px bg-line border-t border-line">
            {INDUSTRY_SERVICE.map((s) => (
              <li
                key={s.body}
                data-testid={`industry-service-${s.body.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-paper p-10 md:p-14"
              >
                <div className="eyebrow text-warm-600 mb-4">{s.role}</div>
                <div className="display text-ink" style={{ fontSize: "clamp(24px, 2.6vw, 40px)" }}>
                  {s.body}
                </div>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* OFFICES */}
      <section className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
        <Reveal>
          <div className="eyebrow mb-6">Offices</div>
          <h2 className="display text-ink mb-12" style={{ fontSize: "clamp(40px, 6vw, 96px)" }}>
            Los Angeles. Dubai. Global.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line">
          {COMPANY.studios.map((s) => (
            <div key={s.city} className="bg-paper p-8 md:p-10">
              <div className="eyebrow text-warm-600">{s.note}</div>
              <div className="mt-3 display text-ink text-2xl md:text-3xl">{s.city}</div>
              <div className="text-warm-600 text-sm mt-1">{s.region}</div>
            </div>
          ))}
        </div>
      </section>
      {/* ARCHIVE — small documentary footer of historical candids. Photos kept
          for the narrative, but framed as the b-roll, not the headline. */}
      <section data-testid="about-archive" className="bg-mute border-t border-line py-20 md:py-28">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14">
          <Reveal>
            <div className="flex flex-wrap items-baseline justify-between gap-6 mb-10">
              <div>
                <div className="eyebrow text-warm-600 mb-2">Archive</div>
                <h2 className="display text-ink" style={{ fontSize: "clamp(28px, 3.5vw, 52px)" }}>
                  Fifteen years on the ground.
                </h2>
              </div>
              <div className="max-w-md text-warm-600 text-sm md:text-base leading-relaxed">
                VidCon, OMR Hamburg, Cannes Lions, Kids&rsquo; Choice Awards,
                Streamy Awards, AliExpress in China — the rooms where the
                work actually happened.
              </div>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <ul className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-7 gap-2 md:gap-3">
              {IN_THE_ROOM.map((p, i) => (
                <li key={i}>
                  <div className="aspect-square overflow-hidden bg-paper">
                    <img
                      src={p.src}
                      alt={p.alt}
                      title={`${p.caption} — ${p.note}`}
                      loading="lazy"
                      className="w-full h-full object-cover grayscale opacity-80 hover:opacity-100 hover:grayscale-0 transition-all duration-500"
                    />
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
